import { Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SentryFeedbackTrigger } from "@/components/sentry/SentryFeedbackTrigger";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { AppLayout, AppShellRouteLoadingState } from "@/components/layout/AppLayout";
import { VendorLayout } from "@/components/layout/VendorLayout";
import { buildAuthRedirectPath } from "@/lib/authReturnTo";
import { isManagedProfileOperator } from "@/lib/supplierProfileStewardship";
import { useOrganization, OrganizationProvider } from "@/contexts/OrganizationContext";
import { useVendor, VendorProvider } from "@/contexts/VendorContext";
import { AgentChatProvider } from "@/contexts/AgentChatContext";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { StepUpProvider } from "@/components/auth/StepUpProvider";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TestRunWidget } from "@/components/TestRunWidget";
import {
  normalizeBotBuddyPublicToken,
  resolveBotBuddyEnvironment,
  resolveBotBuddyTargetUrl,
  shouldEnableBotBuddyWidget,
  shouldRenderBotBuddyWidget,
  shouldSuppressBotBuddyWidgetForPath,
} from "@/lib/botbuddyWidget";
import { appBuildInfo } from "@/lib/buildInfo";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { queryRetryPolicy } from "@/lib/transientBackendError";
import { AppThemeProvider } from "@/components/theme/AppThemeProvider";
import { AdminRouteShell } from "@/components/layout/AdminRouteShell";
import { ShieldAlert } from "lucide-react";
import { AmplitudeAnalyticsBridge } from "@/lib/AmplitudeAnalyticsBridge";
import { AmplitudePageAnalyticsBridge } from "@/lib/AmplitudePageAnalyticsBridge";
import { CustomerFeedbackBridge } from "@/lib/CustomerFeedbackBridge";
import { normalizeDomainInput } from "@/lib/domainNormalization";
import { useWidgetPreferenceDeeplinks, useWidgetPreferences } from "@/lib/widgetPreferences";
import { AdvisoryEmailBoundary, RuntimeAdvisoriesRoute, VendorRuntimeAdvisoriesRoute } from "@/components/advisories/RuntimeAdvisoriesRoute";
import { legacyPoamRedirectTarget } from "@/lib/actionsRoute";
// Loading fallback component
const PageLoader = () => (
  <div className="app-boot-loader">
    <div className="app-boot-loader__spinner" aria-hidden="true" />
    <div className="app-boot-loader__label">Loading</div>
  </div>
);

export function EnterpriseSsoRouteGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading: isSessionLoading } = useOrganization();

  // The pending page resolves rollout from the caller's trusted durable request;
  // this guard keeps the direct request route behind a settled authenticated session.
  if (isSessionLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

export function VendorRouteGuard({ children }: { children: React.ReactNode }) {
  const { organization, user, isLoading } = useOrganization();
  const location = useLocation();
  const {
    loading,
    vendorProfile,
    userRole,
    requestedAdvisoryProfileId,
    error: vendorError,
    refetch: refetchVendor,
    contextVendorProfile = null,
    contextClaimableProfiles = [],
    contextDomain = null,
    contextLoading = false,
  } = useVendor();

  // A supplier claim writes the workspace binding atomically, but an older
  // client-side context read can still be settling while the claim refreshes
  // that workspace. A loading read is not evidence of a mismatch, and protected
  // content remains withheld until the latest workspace-scoped read has settled.
  if (loading || contextLoading) {
    return <PageLoader />;
  }

  // ENT-1958: a signed-out vendor deep link must preserve its destination
  // through /auth BEFORE any claim/profile redirect runs. Without this the
  // page (e.g. VendorDashboard) renders, redirects to /vendor/claim, and the
  // original URL is lost after sign-in. Gate on the auth bootstrap finishing
  // so a still-loading session isn't bounced.
  if (!user && !isLoading) {
    return <Navigate to={buildAuthRedirectPath(location)} replace />;
  }

  // The questionnaire route mounts the full editable response workspace, whose
  // writes and audit events are scoped to the active organisation. Do not admit
  // it from a claimant profile belonging to another workspace; the canonical
  // claimant workspace must hydrate before any vendor page can render.
  if (user && requestedAdvisoryProfileId && vendorError) {
    return <div className="p-6"><Alert><AlertTitle>Supplier profile unavailable</AlertTitle><AlertDescription>
      Could not open this company's supplier profile. Try again.
      <Button onClick={() => void refetchVendor()}>Retry</Button>
    </AlertDescription></Alert></div>;
  }

  // A receipt can select an accepted team profile without granting customer-workspace access.
  if (user && requestedAdvisoryProfileId && vendorProfile?.id === requestedAdvisoryProfileId) {
    return <>{children}</>;
  }

  // ENT-6257 AC5: an operator opens the managed profile it maintains (no claimant workspace).
  if (!organization || contextVendorProfile || isManagedProfileOperator(vendorProfile, userRole)) {
    return <>{children}</>;
  }

  if (contextClaimableProfiles.length > 0) {
    const claimDomain = normalizeDomainInput(contextDomain);
    return (
      <Navigate
        to={`/vendor/claim${claimDomain ? `?domain=${encodeURIComponent(claimDomain)}` : ''}`}
        replace
      />
    );
  }

  const workspaceName = organization.name || 'This workspace';

  return (
    <div className="platform-view-pane min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 dark:from-slate-950 dark:to-slate-900">
      <div className="mx-auto flex min-h-[60vh] max-w-3xl items-center">
        <Alert className="border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-50">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Supplier profile unavailable for this workspace</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>
              {workspaceName} does not have a Supplier Profile or claimable supplier path.
              Switch to a supplier workspace before opening Supplier Profile pages.
            </p>
            <Button asChild variant="outline">
              <a href="/dashboard">Return to current workspace</a>
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}

// Lazy load all pages for better initial bundle size
const Index = lazyWithRetry(() => import("./pages/Index"));
const Navigator = lazyWithRetry(() => import("./pages/Navigator"));
const NavigatorFoundation = lazyWithRetry(() => import("./pages/NavigatorFoundation"));
const NavigatorCrownJewels = lazyWithRetry(() => import("./pages/NavigatorCrownJewels"));
const NavigatorSuppliers = lazyWithRetry(() => import("./pages/NavigatorSuppliers"));
const NavigatorContracts = lazyWithRetry(() => import("./pages/NavigatorContracts"));
const NavigatorSupplyChainBriefing = lazyWithRetry(() => import("./pages/NavigatorSupplyChainBriefing"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const AccessPending = lazyWithRetry(() => import("./pages/AccessPending"));
const Suppliers = lazyWithRetry(() => import("./pages/Suppliers"));
// ENT-3188: the supplier record now opens as a docked drawer inside the Suppliers
// collection (`?peek=<id>`), per the drawer-default surface policy. The legacy
// `/suppliers/:id` route stays as a compatibility redirect that moves the id (and
// any `?tab` intent) into the collection's drawer params, preserving deep links,
// bookmarks, and Navigator hand-offs.
export function SupplierDetailDrawerRedirect() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const legacyTab = searchParams.get('tab');

  if (id) {
    searchParams.set('peek', id);
  }

  if (legacyTab) {
    searchParams.set('peekTab', legacyTab);
  }

  searchParams.delete('tab');

  return <Navigate to={`/suppliers${searchParams.size ? `?${searchParams.toString()}` : ''}`} replace />;
}

export function LegacyPoamRedirect() {
  const location = useLocation();
  return <Navigate to={legacyPoamRedirectTarget(location)} replace />;
}
// SupplierNew removed - all supplier additions flow through Marketplace queue system
const Certifications = lazyWithRetry(() => import("./pages/Certifications"));
const Alerts = lazyWithRetry(() => import("./pages/Alerts"));
const AlertCentre = lazyWithRetry(() => import("./pages/AlertCentre"));
const BreachFeed = lazyWithRetry(() => import("./pages/BreachFeed"));
const BreachNotifications = lazyWithRetry(() => import("./pages/BreachNotifications"));
const Import = lazyWithRetry(() => import("./pages/Import"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const PersonalAccessTokenDocs = lazyWithRetry(() => import("./pages/PersonalAccessTokenDocs"));
const Team = lazyWithRetry(() => import("./pages/Team"));
const OrgTeamJoin = lazyWithRetry(() => import("./pages/OrgTeamJoin"));
const AuthReset = lazyWithRetry(() => import("./pages/AuthReset"));
const AuthConfirm = lazyWithRetry(() => import("./pages/AuthConfirm"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const AdminSignIn = lazyWithRetry(() => import("./pages/admin/AdminSignIn"));
const AdminPasskeySettings = lazyWithRetry(() => import("./components/admin/AdminPasskeySettings").then(module => ({ default: module.AdminPasskeySettings })));
const MarketingContentView = lazyWithRetry(() => import("./pages/admin/MarketingContentView"));
const DataGridHarness = lazyWithRetry(() => import("./pages/admin/DataGridHarness"));
const AdminStorybook = lazyWithRetry(() => import("./pages/admin/AdminStorybook"));
const BreachIntelScanInspector = lazyWithRetry(() => import("./pages/admin/BreachIntelScanInspector"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const McpAuth = lazyWithRetry(() => import("./pages/McpAuth"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Marketplace = lazyWithRetry(() => import("./pages/Marketplace"));
const Poam = lazyWithRetry(() => import("./pages/Poam"));
const TeleLens = lazyWithRetry(() => import("./pages/TeleLens"));
const TeleLensSupplier = lazyWithRetry(() => import("./pages/TeleLensSupplier"));
const TeleLensJoin = lazyWithRetry(() => import("./pages/TeleLensJoin"));
const TeleLensBriefingAccess = lazyWithRetry(() => import("./pages/TeleLensBriefingAccess"));
const Customers = lazyWithRetry(() => import("./pages/Customers"));
const MSPInsights = lazyWithRetry(() => import("./pages/MSPInsights"));
const MSPHeatmap = lazyWithRetry(() => import("./pages/MSPHeatmap"));
const MSPTemplates = lazyWithRetry(() => import("./pages/MSPTemplates"));
const PitchBook = lazyWithRetry(() => import("./pages/PitchBook"));
const PitchBookBriefing = lazyWithRetry(() => import("./pages/PitchBookBriefing"));
const Insights = lazyWithRetry(() => import("./pages/Insights"));
const Dependencies = lazyWithRetry(() => import("./pages/Dependencies"));
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const PlanSelection = lazyWithRetry(() => import("./pages/PlanSelection"));
const SupplierPlusUpgrade = lazyWithRetry(() => import("./pages/SupplierPlusUpgrade"));
const PublicQuestionnaireShare = lazyWithRetry(() => import("./pages/PublicQuestionnaireShare"));
const PublicCustomerBriefingShare = lazyWithRetry(() => import("./pages/PublicCustomerBriefingShare"));
const PublicProspectBriefingShare = lazyWithRetry(() => import("./pages/PublicProspectBriefingShare"));
const SentryTest = lazyWithRetry(() => import("./pages/SentryTest"));

// Vendor Portal Pages
const VendorDashboard = lazyWithRetry(() => import("./pages/vendor/VendorDashboard"));
const VendorClaim = lazyWithRetry(() => import("./pages/vendor/VendorClaim"));
const VendorClaimAuth = lazyWithRetry(() => import("./pages/vendor/VendorClaimAuth"));
const VendorProfile = lazyWithRetry(() => import("./pages/vendor/VendorProfile"));
const VendorCertifications = lazyWithRetry(() => import("./pages/vendor/VendorCertifications"));
const VendorPolicies = lazyWithRetry(() => import("./pages/vendor/VendorPolicies"));
const VendorQuestionnaires = lazyWithRetry(() => import("./pages/vendor/VendorQuestionnaires"));
const VendorActions = lazyWithRetry(() => import("./pages/vendor/VendorActions"));
const VendorBreachNotifications = lazyWithRetry(() => import("./pages/vendor/VendorBreachNotifications"));
const VendorQuestionnaireResponse = lazyWithRetry(() => import("./pages/vendor/VendorQuestionnaireResponse"));
const VendorCustomQuestionnaireResponse = lazyWithRetry(() => import("./pages/vendor/VendorCustomQuestionnaireResponse"));
const CustomQuestionnaires = lazyWithRetry(() => import("./pages/CustomQuestionnaires"));
const VendorVisibility = lazyWithRetry(() => import("./pages/vendor/VendorVisibility"));
const VendorTeam = lazyWithRetry(() => import("./pages/vendor/VendorTeam"));
const VendorTeamJoin = lazyWithRetry(() => import("./pages/vendor/VendorTeamJoin"));
const VendorSettings = lazyWithRetry(() => import("./pages/vendor/VendorSettings"));
// ENT-5889: runtime advisories (customer/MSP page, supplier portal, public check, admin console).
const Advisory = lazyWithRetry(() => import("./pages/Advisory"));
const AdvisoryEmailEntry = lazyWithRetry(() => import("./pages/AdvisoryEmailEntry"));
const Advisories = lazyWithRetry(() => import("./pages/Advisories"));
// Route adapters preserve the shared page while the lazy loader accepts prop-free routes.
const MspAdvisory = lazyWithRetry(() => import("./pages/Advisory").then(({ default: Page }) => ({ default: () => <Page mspView /> })));
const MspAdvisories = lazyWithRetry(() => import("./pages/Advisories").then(({ default: Page }) => ({ default: () => <Page mspView /> })));
const VendorAdvisory = lazyWithRetry(() => import("./pages/vendor/VendorAdvisory"));
const VendorAdvisories = lazyWithRetry(() => import("./pages/vendor/VendorAdvisories"));
const PublicRuntimeCheck = lazyWithRetry(() => import("./pages/PublicRuntimeCheck"));
const AdminAdvisoryConsole = lazyWithRetry(() => import("./pages/AdminAdvisoryConsole"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      // Reads can safely ride out deploy-window blips; mutations remain at TanStack's default retry: 0.
      retry: queryRetryPolicy,
    },
  },
});

// Safety net: prevent blank screens from uncaught async errors (e.g. provisioning flows)
const GlobalUnhandledRejectionHandler = () => {
  const { toast } = useToast();

  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      toast({
        title: 'Something went wrong',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
      // Prevent the default browser handling which can manifest as a blank screen
      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, [toast]);

  return null;
};

/**
 * Resolve the BotBuddy test run token.
 *
 * CI: baked in at build time via VITE_BOTBUDDY_TEST_RUN_TOKEN.
 * Local dev: agents write public/.botbuddy-token which Vite serves as a static file.
 */
function useBotBuddyToken(): string | null {
  const envToken = normalizeBotBuddyPublicToken(import.meta.env.VITE_BOTBUDDY_TEST_RUN_TOKEN);
  const [fileToken, setFileToken] = useState<string | null>(null);

  useEffect(() => {
    if (envToken) return; // valid env token takes priority, skip file fetch
    fetch("/.botbuddy-token")
      .then((res) => (res.ok ? res.text() : null))
      .then((text) => {
        setFileToken(normalizeBotBuddyPublicToken(text));
      })
      .catch(() => {});
  }, [envToken]);

  return envToken ?? fileToken;
}

/** Extract ticket ID (e.g. "ENT-748") from a branch name like "ent-748-botbuddy-token". */
function extractTicketId(branchName?: string): string | null {
  if (!branchName) return null;
  const match = branchName.match(/\b([A-Z]+-\d+)\b/i);
  return match ? match[1].toUpperCase() : null;
}

export const BotBuddyWidgetBridge = ({
  showBotBuddyWidget,
  botBuddyEnvironment,
  botBuddyCommitSha,
  botBuddyBranchName,
  botBuddyPrNumber,
  botBuddyTicketId,
}: {
  showBotBuddyWidget: boolean;
  botBuddyEnvironment?: string;
  botBuddyCommitSha: string;
  botBuddyBranchName?: string;
  botBuddyPrNumber?: number;
  botBuddyTicketId: string | null;
}) => {
  const location = useLocation();
  const testRunToken = useBotBuddyToken();

  if (
    shouldSuppressBotBuddyWidgetForPath(location.pathname) ||
    !shouldRenderBotBuddyWidget(showBotBuddyWidget, testRunToken)
  ) {
    return null;
  }

  const targetUrl = resolveBotBuddyTargetUrl(
    {
      href: window.location.href,
      origin: window.location.origin,
      hostname: window.location.hostname,
    },
    import.meta.env.VITE_SENTRY_ENVIRONMENT,
    import.meta.env.MODE,
  );

  return (
    <TestRunWidget
      appSlug="supplyspark-ent"
      environment={botBuddyEnvironment}
      ticketId={botBuddyTicketId ?? undefined}
      targetUrl={targetUrl}
      version={appBuildInfo.version}
      commitSha={botBuddyCommitSha}
      branchName={botBuddyBranchName}
      prNumber={botBuddyPrNumber}
      testRunToken={testRunToken}
      defaultPosition={{ x: 520, y: 16 }}
    />
  );
};

const WidgetPreferenceDeeplinkBridge = () => {
  const location = useLocation();
  useWidgetPreferenceDeeplinks(location.search);
  return null;
};

/**
 * Supplier Profile (vendor) portal routes.
 *
 * ENT-3079: every `/vendor/*` page shares ONE persistent `VendorLayout` mounted
 * at the route level via an `<Outlet />`, exactly like the customer portal shares
 * `AppLayout`. This stops the layout (and its ENT-2770 step-up gate) remounting on
 * every menu click — which on MFA-enforced staging/production flashed the whole
 * "Verifying your two-factor session…" shell as if the page had hard-reloaded.
 *
 * `/vendor/claim*` and `/vendor/team/join` stay OUTSIDE the shared shell: they are
 * pre-authorization / invite surfaces that must render without the vendor chrome
 * or the supplier-context route guard.
 *
 * Rendered as `{VendorPortalRoutes()}` (not `<VendorPortalRoutes />`) so the
 * returned `<Route>` fragments are direct children of the parent `<Routes>`.
 */
export function VendorPortalRoutes() {
  return (
    <>
      <Route path="/vendor/claim/auth" element={<VendorClaimAuth />} />
      <Route path="/vendor/claim" element={<VendorClaim />} />
      <Route path="/vendor/team/join" element={<VendorTeamJoin />} />
      <Route
        path="/vendor"
        element={
          <VendorRouteGuard>
            <VendorLayout>
              {/* In-shell chunk fallback — canonical parity with AppLayout's inner
                  Suspense: a lazy vendor page loading must never bubble to the
                  app-level boundary and unmount the shell. */}
              <Suspense fallback={<AppShellRouteLoadingState message="Loading page…" />}>
                <Outlet />
              </Suspense>
            </VendorLayout>
          </VendorRouteGuard>
        }
      >
        <Route index element={<VendorDashboard />} />
        <Route path="profile" element={<VendorProfile />} />
        <Route path="certifications" element={<VendorCertifications />} />
        <Route path="policies" element={<VendorPolicies />} />
        <Route path="questionnaires" element={<VendorQuestionnaires />} />
        <Route path="questionnaires/custom/:requestId" element={<VendorCustomQuestionnaireResponse />} />
        <Route path="questionnaires/:id" element={<VendorQuestionnaireResponse />} />
        <Route path="visibility" element={<VendorVisibility />} />
        <Route path="actions" element={<VendorActions />} />
        {/* ENT-5889: gated on the claimant organisation's runtime_advisories flag (fail closed). */}
        <Route path="advisories" element={<VendorRuntimeAdvisoriesRoute><VendorAdvisories /></VendorRuntimeAdvisoriesRoute>} />
        <Route path="advisories/:advisoryId" element={<VendorRuntimeAdvisoriesRoute><VendorAdvisory /></VendorRuntimeAdvisoriesRoute>} />
        <Route path="breach-notifications" element={<VendorBreachNotifications />} />
        <Route path="team" element={<VendorTeam />} />
        <Route path="settings" element={<VendorSettings scope="personal" />} />
        <Route path="settings/organisation" element={<VendorSettings scope="organisation" />} />
      </Route>
    </>
  );
}

/**
 * Customer + MSP portal routes.
 *
 * Every route element wraps its page in the SAME `<AppLayout>` component at the
 * route level, so React reconciliation keeps ONE AppLayout instance mounted
 * across client-side navigation (the layout type never changes at this tree
 * position — only the page child swaps). This is the canonical persistence
 * mechanism the Supplier Profile portal now mirrors via its `/vendor` layout
 * route (ENT-3079). AppLayout wraps routes explicitly — never a pathless layout
 * route — so vendor URLs cannot be captured by it.
 *
 * Rendered as `{CustomerPortalRoutes()}` (not `<CustomerPortalRoutes />`) so the
 * returned `<Route>` fragments are direct children of the parent `<Routes>`.
 */
export function CustomerPortalRoutes() {
  return (
    <>
      <Route path="/dashboard" element={<AppLayout><Navigate to="/navigator" replace /></AppLayout>} />
      <Route path="/navigator" element={<AppLayout><Navigator /></AppLayout>} />
      <Route path="/navigator/foundation" element={<AppLayout><NavigatorFoundation /></AppLayout>} />
      <Route path="/navigator/crown-jewels" element={<AppLayout><NavigatorCrownJewels /></AppLayout>} />
      <Route path="/navigator/suppliers" element={<AppLayout><NavigatorSuppliers /></AppLayout>} />
      <Route path="/navigator/contracts" element={<AppLayout><NavigatorContracts /></AppLayout>} />
      <Route path="/navigator/supply-chain-briefing" element={<AppLayout><NavigatorSupplyChainBriefing /></AppLayout>} />
      <Route path="/insights" element={<AppLayout><Insights /></AppLayout>} />
      <Route path="/suppliers" element={<AppLayout><Suppliers /></AppLayout>} />
      <Route path="/suppliers/new" element={<AppLayout><Navigate to="/marketplace" replace /></AppLayout>} />
      <Route path="/suppliers/:id" element={<AppLayout><SupplierDetailDrawerRedirect /></AppLayout>} />
      <Route path="/certifications" element={<AppLayout><Certifications /></AppLayout>} />
      <Route path="/breach-feed" element={<AppLayout><BreachFeed /></AppLayout>} />
      <Route path="/breach-notifications" element={<AppLayout><BreachNotifications /></AppLayout>} />
      <Route path="/alert-centre" element={<AppLayout><AlertCentre /></AppLayout>} />
      <Route path="/alerts" element={<AppLayout><Alerts /></AppLayout>} />
      {/* ENT-6091: separate active-org and managed-customer scopes, checked before page reads. */}
      <Route path="/msp/advisories" element={<AppLayout><RuntimeAdvisoriesRoute mspView><MspAdvisories /></RuntimeAdvisoriesRoute></AppLayout>} />
      <Route path="/msp/advisories/:advisoryId" element={<AppLayout><RuntimeAdvisoriesRoute mspView><MspAdvisory /></RuntimeAdvisoriesRoute></AppLayout>} />
      <Route path="/advisories" element={<AppLayout><RuntimeAdvisoriesRoute><Advisories /></RuntimeAdvisoriesRoute></AppLayout>} />
      <Route path="/advisories/:advisoryId" element={<AppLayout><RuntimeAdvisoriesRoute><Advisory /></RuntimeAdvisoriesRoute></AppLayout>} />
      <Route path="/settings" element={<AppLayout><Settings scope="personal" /></AppLayout>} />
      <Route path="/settings/organisation" element={<AppLayout><Settings scope="organisation" /></AppLayout>} />
      <Route path="/docs" element={<AppLayout><PersonalAccessTokenDocs /></AppLayout>} />
      <Route path="/team" element={<AppLayout><Team /></AppLayout>} />
      <Route path="/marketplace" element={<AppLayout><Marketplace /></AppLayout>} />
      <Route path="/actions" element={<AppLayout><Poam /></AppLayout>} />
      <Route path="/poam" element={<LegacyPoamRedirect />} />
      <Route path="/telelens" element={<AppLayout><TeleLens /></AppLayout>} />
      <Route path="/customers" element={<AppLayout><Customers /></AppLayout>} />
      <Route path="/msp-insights" element={<AppLayout><MSPInsights /></AppLayout>} />
      <Route path="/msp-heatmap" element={<AppLayout><MSPHeatmap /></AppLayout>} />
      <Route path="/msp-templates" element={<AppLayout><MSPTemplates /></AppLayout>} />
      <Route path="/pitch-book" element={<AppLayout><PitchBook /></AppLayout>} />
      <Route path="/pitch-book/briefing/:domain" element={<PitchBookBriefing />} />
      <Route path="/dependencies" element={<AppLayout><Dependencies /></AppLayout>} />
      <Route path="/reports" element={<AppLayout><Reports /></AppLayout>} />
      <Route path="/questionnaires/custom" element={<AppLayout><CustomQuestionnaires /></AppLayout>} />
      <Route path="/import" element={<AppLayout><Import /></AppLayout>} />
    </>
  );
}

const App = () => {
  const sentryTestRouteEnabled =
    import.meta.env.VITE_SENTRY_TEST_ROUTE === "true" && import.meta.env.VITE_SENTRY_ENVIRONMENT !== "production";
  const rawBotBuddyEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE;
  const botBuddyPrNumber = Number.parseInt(import.meta.env.VITE_BOTBUDDY_PR_NUMBER ?? "", 10);
  const botBuddyBranchName = import.meta.env.VITE_BOTBUDDY_BRANCH_NAME?.trim() || undefined;
  const botBuddyCommitSha = import.meta.env.VITE_BOTBUDDY_COMMIT_SHA?.trim() || appBuildInfo.commitSha;
  const botBuddyEnvironment = resolveBotBuddyEnvironment(
    import.meta.env.VITE_SENTRY_ENVIRONMENT,
    import.meta.env.MODE,
    window.location.hostname,
  );
  const showBotBuddyWidget = shouldEnableBotBuddyWidget(window.location.hostname, rawBotBuddyEnvironment);
  const botBuddyWidgetDisabled =
    botBuddyEnvironment === "local" && import.meta.env.VITE_BOTBUDDY_DISABLE_WIDGET === "true";
  const botBuddyTicketId =
    import.meta.env.VITE_BOTBUDDY_TICKET_ID?.trim() || extractTicketId(botBuddyBranchName) || null;
  const { preferences: widgetPreferences } = useWidgetPreferences();

  return (
    <AppThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <OrganizationProvider>
            <StepUpProvider>
            <SubscriptionProvider>
              <AgentChatProvider>
                <CustomerFeedbackBridge />
                {widgetPreferences.sentry && <SentryFeedbackTrigger />}
                <Toaster />
                <Sonner />
                <SpeedInsights />
                <GlobalUnhandledRejectionHandler />
                <ErrorBoundary>
                  <BrowserRouter>
                    <WidgetPreferenceDeeplinkBridge />
                    <AmplitudeAnalyticsBridge />
                    <AmplitudePageAnalyticsBridge />
                    <BotBuddyWidgetBridge
                      showBotBuddyWidget={showBotBuddyWidget && !botBuddyWidgetDisabled}
                      botBuddyEnvironment={botBuddyEnvironment}
                      botBuddyTicketId={botBuddyTicketId}
                      botBuddyCommitSha={botBuddyCommitSha}
                      botBuddyBranchName={botBuddyBranchName}
                      botBuddyPrNumber={Number.isFinite(botBuddyPrNumber) ? botBuddyPrNumber : undefined}
                    />
                    <VendorProvider>
                      <AdvisoryEmailBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/onboarding" element={<Onboarding />} />
                      <Route path="/access-pending" element={<EnterpriseSsoRouteGuard><AccessPending /></EnterpriseSsoRouteGuard>} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/admin/sign-in" element={<AdminSignIn />} />
                      <Route path="/admin/security/passkeys" element={<AdminRouteShell><AdminPasskeySettings /></AdminRouteShell>} />
                      <Route path="/advisories/email/:advisoryId" element={<AdvisoryEmailEntry />} />
                      {/* ENT-4311: scanner-safe password reset — token_hash lands here; verify only on the explicit click. */}
                      <Route path="/auth/reset" element={<AuthReset />} />
                      {/* ENT-4312: scanner-safe signup / email-change confirmation — token_hash lands here; verify only on the explicit click. */}
                      <Route path="/auth/confirm" element={<AuthConfirm />} />
                      <Route path="/mcp-auth" element={<McpAuth />} />
                      <Route path="/share/questionnaires/:shareToken" element={<PublicQuestionnaireShare />} />
                      <Route path="/share/customer-briefings/:shareToken" element={<PublicCustomerBriefingShare />} />
                      <Route path="/share/prospect-briefings/:shareToken" element={<PublicProspectBriefingShare />} />
                      <Route path="/telelens/supplier" element={<TeleLensSupplier />} />
                      <Route path="/telelens/join" element={<TeleLensJoin />} />
                      <Route path="/telelens/briefing" element={<TeleLensBriefingAccess />} />
                      <Route path="/team/join" element={<OrgTeamJoin />} />
                      <Route path="/plan-selection" element={<PlanSelection />} />
                      <Route path="/upgrade/supplier-plus" element={<SupplierPlusUpgrade />} />
                      <Route path="/admin" element={<AdminRouteShell><Admin /></AdminRouteShell>} />
                      <Route path="/admin/data-grid-harness" element={<AdminRouteShell><DataGridHarness /></AdminRouteShell>} />
                      <Route path="/admin/storybook" element={<AdminRouteShell><AdminStorybook /></AdminRouteShell>} />
                      <Route path="/admin/supplier-domains/:id/scans" element={<AdminRouteShell><BreachIntelScanInspector /></AdminRouteShell>} />
                      <Route path="/admin/content/:id" element={<AdminRouteShell><MarketingContentView /></AdminRouteShell>} />
                      <Route path="/admin/advisories/:advisoryId" element={<AdminRouteShell><AdminAdvisoryConsole /></AdminRouteShell>} />
                      {/* ENT-5889 (D8): public, unauthenticated, rate-limited dated runtime verdicts. */}
                      <Route path="/check/:domain" element={<PublicRuntimeCheck />} />
                      {sentryTestRouteEnabled && <Route path="/__sentry-test" element={<SentryTest />} />}

                     {/* Vendor (Supplier Profile) Portal Routes — see VendorPortalRoutes (ENT-3079). */}
                     {VendorPortalRoutes()}

                      {/* Customer + MSP portal routes — see CustomerPortalRoutes (ENT-3079). */}
                      {CustomerPortalRoutes()}

                        <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                      </AdvisoryEmailBoundary>
                    </VendorProvider>
                  </BrowserRouter>
                </ErrorBoundary>
              </AgentChatProvider>
            </SubscriptionProvider>
            </StepUpProvider>
          </OrganizationProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </AppThemeProvider>
  );
};

export default App;
